
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { CookieConsentOptions } from './CookieConsent';
import { useTranslation } from 'react-i18next';

interface CookieSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (options: CookieConsentOptions) => void;
  onForgetMe: () => void;
  initialOptions: CookieConsentOptions;
}

const CookieSettings: React.FC<CookieSettingsProps> = ({
  isOpen,
  onClose,
  onSave,
  onForgetMe,
  initialOptions,
}) => {
  const { t } = useTranslation('cookies');
  const [options, setOptions] = useState<CookieConsentOptions>({...initialOptions});

  const handleOptionChange = (key: keyof CookieConsentOptions) => {
    if (key === 'necessary') return;
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('settings.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <p className="text-sm text-muted-foreground">
            {t('settings.intro')}
          </p>

          {/* Necessary */}
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{t('settings.necessary.title')}</div>
              <div className="text-sm text-muted-foreground">
                {t('settings.necessary.description')}
              </div>
              <div className="text-xs text-muted-foreground mt-1 italic">
                {t('settings.necessary.details')}
              </div>
            </div>
            <Switch checked={options.necessary} disabled />
          </div>

          {/* Preferences */}
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{t('settings.preferences.title')}</div>
              <div className="text-sm text-muted-foreground">
                {t('settings.preferences.description')}
              </div>
              <div className="text-xs text-muted-foreground mt-1 italic">
                {t('settings.preferences.details')}
              </div>
            </div>
            <Switch
              checked={options.preferences}
              onCheckedChange={() => handleOptionChange('preferences')}
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between sm:gap-0">
          <Button variant="outline" onClick={onForgetMe} className="order-2 sm:order-1">
            {t('settings.forgetMe')}
          </Button>
          <div className="flex gap-2 order-1 sm:order-2">
            <Button variant="outline" onClick={onClose}>
              {t('settings.cancel')}
            </Button>
            <Button onClick={() => onSave(options)}>
              {t('settings.save')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CookieSettings;
